import _ from "lodash";
import React from "react";
/**--external-- */
import { graphql } from "lib/apolloClient";
import ACLStore from "lib/aclStore";
import { useQuery } from "@apollo/client";
/**--internal-- */
import { GRAPHQL_ONGOING_QUERY_NETWORK_STATUS } from "Constants";

/**--relative-- */
import {
  getTemplateFieldsFromCache,
  getFlatFeedUnitPlansFromCache,
} from "./UnitPlanGraphqlHelpers";
import {
  getTemplateFieldsQuery,
  getFlatUnitPlanFeedV2Query,
  getUserCoursesQuery,
  getPlannerElementNodeSetIdsOfCurriculumQuery,
  getPlannerElementNodeSetsFromIdsQuery,
} from "./UnitPlanQuery";
import {
  generateTemplateFieldQueryVariables,
  generateFieldListFromTemplateFields,
  getFlatUnitPlansMemoize,
  getTeachingHoursMemoize,
  getActiveCoursesMemoized,
} from "./Utils";
import { getMergedPlannerElementNodeSets } from "./UnitPlanModule/UnitPlanUtils";
import {
  CURRICULUM_TYPE_PYP,
  CURRICULUM_TYPE_DP,
} from "Constants/stringConstants";

const getUnitTypeCount = ({ unitTypeAndCount }) => {
  return _.reduce(
    unitTypeAndCount,
    (result, val) => {
      result[val.type] = val.count;
      return result;
    },
    {}
  );
};
export const getUnitTypeCountMemoize = _.memoize(
  params => getUnitTypeCount(params),
  params => JSON.stringify(params)
);

const _defaultCallback = ({ data, ownProps }) => {
  const { templateIds } = ownProps;

  return {
    fieldLists: _.reduce(
      templateIds,
      (result, templateId) => {
        const template = _.find(data, { id: templateId });
        if (!template) {
          return result;
        }

        const templateFields = _.get(template, ["templateField", "edges"]);
        result[templateId] = generateFieldListFromTemplateFields({
          templateFields,
          templateId,
        });

        return result;
      },
      {}
    ),
  };
};

export const getTemplateFieldQueryEnhancer = ({
  callback = _defaultCallback,
  skipCondition = () => false,
} = {}) => {
  return graphql(getTemplateFieldsQuery, {
    skip: ({ templateIds, curriculumProgramId, ...ownProps }) => {
      const defaultSkip =
        _.isEmpty(_.compact(templateIds)) || _.isEmpty(curriculumProgramId);

      const dynamicSkip = skipCondition(ownProps);

      return defaultSkip || dynamicSkip;
    },
    name: "getTemplateFields",
    alias: "getTemplateFields",
    options: ({ templateIds, i18n, curriculumProgramId }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: generateTemplateFieldQueryVariables({
          templateIds,
          locale: i18n.language,
          curriculumProgramId,
        }),
      };
    },
    props: ({ getTemplateFields: { networkStatus }, ownProps }) => {
      const {
        isData,
        isLoading,
        templateIds,
        i18n,
        curriculumProgramId,
      } = ownProps;

      const data = getTemplateFieldsFromCache(
        generateTemplateFieldQueryVariables({
          templateIds,
          locale: i18n.language,
          curriculumProgramId,
        })
      );

      return {
        isData: !_.isEmpty(data) && isData,
        isLoading:
          (_.includes(GRAPHQL_ONGOING_QUERY_NETWORK_STATUS, networkStatus) &&
            _.isEmpty(data)) ||
          isLoading,
        ...callback({ data, ownProps }),
      };
    },
  });
};

export const getFlatUnitPlanFeedV2QueryEnhancer = graphql(
  getFlatUnitPlanFeedV2Query,
  {
    name: "getFlatUnitPlanFeedV2",
    alias: "getFlatUnitPlanFeedV2",
    skip: ({
      curriculumType,
      courseId,
      skipGetFlatUnitPlans = false,
      learningCourseId,
    }) =>
      skipGetFlatUnitPlans ||
      (_.isEmpty(courseId) && learningCourseId) ||
      (_.isEmpty(courseId) || _.isEqual(curriculumType, CURRICULUM_TYPE_PYP)
        ? ACLStore.can("TeacherPortal:EnableYearlySchedulerInPYP")
        : false),

    options: ({
      fieldUIDs,
      subjectGroupList,
      gradeList,
      getFlatUnitPlanFeedV2QueryVariables,
      courseId,
    }) => {
      return {
        fetchPolicy: "cache-and-network",
        variables: getFlatUnitPlanFeedV2QueryVariables({
          fieldUIDs,
          subjectGroupList,
          gradeList,
          courseId,
        }),
        context: {
          useHttpLink: true,
        },
      };
    },
    props({
      getFlatUnitPlanFeedV2,
      ownProps: { isData, isLoading, unitTypeOptions, curriculumType },
    }) {
      const data = getFlatFeedUnitPlansFromCache(
        getFlatUnitPlanFeedV2.variables
      );
      const pageInfo = _.get(data, "node.flatUnitPlans.pageInfo", {});
      const flatUnitPlans = _.get(data, "node.flatUnitPlans.edges", []);
      const allUnitPlans = _.map(flatUnitPlans, plan => ({
        fieldValue: _.get(plan.node, "unitType.value"),
        node: plan.node,
      }));

      // unitplans logic for groupby feeds
      let unitPlans = _.map(unitTypeOptions, opt => ({
        fieldValue: opt.value,
        node: _.filter(
          _.map(flatUnitPlans, plan => plan.node),
          plan => _.get(plan, "unitType.value") === opt.value
        ),
      }));

      unitPlans = _.filter(unitPlans, ({ node }) => {
        return node.length;
      });
      const unitPlansCount = _.get(data, "node.flatUnitPlans.totalCount", 0);
      const unitTypeCount = getUnitTypeCountMemoize({
        unitTypeAndCount: _.get(data, "node.flatUnitPlans.unitTypeCount", {}),
      });

      // yearly schedule logic for courseOverview and timeLineTable
      let yearlyUnitPlans = [];
      let slTeachingHours = 0;
      let hlTeachingHours = 0;
      let totalTeachingHours = 0;

      yearlyUnitPlans = getFlatUnitPlansMemoize({
        edges: _.get(data, "node.flatUnitPlans.edges", []),
      });

      if (_.isEqual(curriculumType, CURRICULUM_TYPE_DP)) {
        slTeachingHours = getTeachingHoursMemoize({
          unitPlans: yearlyUnitPlans,
          label: "SL",
        });

        hlTeachingHours = getTeachingHoursMemoize({
          unitPlans: yearlyUnitPlans,
          label: "HL",
        });

        totalTeachingHours = getTeachingHoursMemoize({
          unitPlans: yearlyUnitPlans,
          label: "totalHours",
        });
      }
      return {
        getFlatUnitPlanFeedV2,
        onPusherEventTriggered: getFlatUnitPlanFeedV2.refetch,
        unitPlansCount,
        unitTypeOptions,
        unitPlans,
        allUnitPlans,
        unitTypeCount,
        flatUnitPlans,
        yearlySchedule: yearlyUnitPlans,
        slTeachingHours,
        hlTeachingHours,
        totalTeachingHours,
        yearlyGroupByUnitPlans: unitPlans,
        unitPlansTotalCount: unitPlansCount,
        networkStatus: getFlatUnitPlanFeedV2.networkStatus,
        loadMoreFeed: () => {
          return getFlatUnitPlanFeedV2.fetchMore({
            variables: {
              ...getFlatUnitPlanFeedV2.variables,
              after: pageInfo.endCursor,
            },

            updateQuery: (previousResult, { fetchMoreResult }) => {
              const previousItems = previousResult.node.flatUnitPlans;
              const nextItems = fetchMoreResult.node.flatUnitPlans;

              const previousEdges = previousItems.edges;
              const nextEdges = nextItems.edges;

              const previousPageInfo = previousItems.pageInfo;
              const nextPageInfo = nextItems.pageInfo;

              const nextEndCursor = nextPageInfo.endCursor;
              const nextHasNextPage = nextPageInfo.hasNextPage;

              return {
                ...previousResult,
                node: {
                  ...previousResult.node,
                  flatUnitPlans: {
                    ...previousItems,
                    edges: [...previousEdges, ...nextEdges],
                    pageInfo: {
                      ...previousPageInfo,
                      endCursor: nextEndCursor,
                      hasNextPage: nextHasNextPage,
                    },
                  },
                },
              };
            },
          });
        },
        hasNextPage: pageInfo.hasNextPage,
        areMorePlansLoading: getFlatUnitPlanFeedV2.networkStatus === 3,
        isData: isData && !_.isEmpty(data),
        isUnitPlanData: isData && !_.isEmpty(data),
        isLoading:
          isLoading ||
          (_.includes([1, 2], getFlatUnitPlanFeedV2.networkStatus) &&
            _.isEmpty(data)),
      };
    },
  }
);

export const useUserCourses = userCoursesVariables => {
  const { shouldSkipUserCourseQuery, ...variables } = userCoursesVariables;

  const { data, loading, networkStatus } = useQuery(getUserCoursesQuery, {
    fetchPolicy: "cache-and-network",
    skip: shouldSkipUserCourseQuery,
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const queryData = _.get(data, "node", {});
  const courses = _.get(queryData, "courses", []);
  const activeCourses = getActiveCoursesMemoized({
    userCoursesData: queryData,
  });

  const isLoading = _.includes([1, 2], networkStatus) || loading;
  const isData = !_.isEmpty(data);

  return {
    courses,
    activeCourses,
    userCourses: queryData,
    userCoursesLoading: !shouldSkipUserCourseQuery && isLoading,
    userCoursesData: shouldSkipUserCourseQuery || isData,
  };
};

export const getUserCoursesQueryEnhancer = Component => {
  const UserCoursesWrapper = props => {
    const { userCoursesVariables } = props;
    const { shouldSkipUserCourseQuery } = userCoursesVariables;
    const userCoursesData = useUserCourses(userCoursesVariables);

    if (shouldSkipUserCourseQuery) {
      return <Component {...props} />;
    }
    return <Component {...props} {...userCoursesData} />;
  };

  return UserCoursesWrapper;
};

export const getPlannerElementNodeSetIdsOfCurriculumVariables = ({
  curriculumId,
  curriculumNodeInterfaceType,
  allPlannerElementTypes,
  isToddleLibrary = false,
}) => {
  const plannerElementNodeSetFilters = {
    types: allPlannerElementTypes,
  };
  return {
    id: curriculumId,
    type: curriculumNodeInterfaceType,
    plannerElementNodeSetFilters,
    ...(isToddleLibrary && {
      portalType: "COMMUNITY",
    }),
  };
};

export const getPlannerElementNodeSetIdsOfCurriculumEnhancer = graphql(
  getPlannerElementNodeSetIdsOfCurriculumQuery,
  {
    name: "getPlannerElementNodeSetIdsOfCurriculum",
    options: ({
      curriculumId,
      curriculumNodeInterfaceType,
      allPlannerElementTypes,
      isToddleLibrary,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPlannerElementNodeSetIdsOfCurriculumVariables({
        curriculumId,
        curriculumNodeInterfaceType,
        allPlannerElementTypes,
        isToddleLibrary,
      }),
    }),
    props({
      getPlannerElementNodeSetIdsOfCurriculum,
      ownProps: { isData, isLoading },
    }) {
      const data = _.get(getPlannerElementNodeSetIdsOfCurriculum, "node", {});
      const plannerElementNodeSets = _.map(
        _.get(data, "plannerElementNodeSets.edges", []),
        "node"
      );

      return {
        plannerElementNodeSets,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getPlannerElementNodeSetIdsOfCurriculum["networkStatus"] == 1 ||
          getPlannerElementNodeSetIdsOfCurriculum["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }
);

export const getPlannerElementNodeSetIdsOfBaseCurriculumEnhancer = graphql(
  getPlannerElementNodeSetIdsOfCurriculumQuery,
  {
    name: "getPlannerElementNodeSetIdsOfBaseCurriculum",
    skip: ({ baseCurriculumId }) => _.isEmpty(baseCurriculumId),
    options: ({
      baseCurriculumId,
      baseCurriculumNodeInterfaceType,
      allPlannerElementTypes,
      isToddleLibrary,
    }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPlannerElementNodeSetIdsOfCurriculumVariables({
        curriculumId: baseCurriculumId,
        curriculumNodeInterfaceType: baseCurriculumNodeInterfaceType,
        allPlannerElementTypes,
        isToddleLibrary,
      }),
    }),
    props({
      getPlannerElementNodeSetIdsOfBaseCurriculum,
      ownProps: { isData, isLoading, plannerElementNodeSets = [] },
    }) {
      const data = _.get(
        getPlannerElementNodeSetIdsOfBaseCurriculum,
        "node",
        {}
      );
      const baseCurriculumNodeSets = _.map(
        _.get(data, "plannerElementNodeSets.edges", []),
        "node"
      );

      return {
        plannerElementNodeSets: getMergedPlannerElementNodeSets(
          plannerElementNodeSets,
          baseCurriculumNodeSets
        ),
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getPlannerElementNodeSetIdsOfBaseCurriculum["networkStatus"] == 1 ||
          getPlannerElementNodeSetIdsOfBaseCurriculum["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }
);

export const getPlannerElementNodeSetsFromIdsVariables = ({
  plannerElementNodeSets,
  plannerElementNodeFilters,
  academicYearId,
}) => {
  const plannerElementNodeSetIds = _.map(plannerElementNodeSets, "id");
  return {
    plannerElementNodeSetIds,
    plannerElementNodeFilters,
    ...(academicYearId ? { academicYearId } : {}),
  };
};

export const getPlannerElementNodeSetsFromIdsEnhancer = graphql(
  getPlannerElementNodeSetsFromIdsQuery,
  {
    name: "getPlannerElementNodeSetsFromIds",
    skip: ({ plannerElementNodeSets, skipQuery }) => {
      return _.isEmpty(plannerElementNodeSets) || !!skipQuery;
    },
    options: ({ plannerElementNodeSets, plannerElementNodeFilters }) => ({
      fetchPolicy: "cache-and-network",
      variables: getPlannerElementNodeSetsFromIdsVariables({
        plannerElementNodeSets,
        plannerElementNodeFilters,
      }),
    }),
    props({
      getPlannerElementNodeSetsFromIds,
      ownProps: { isData, isLoading },
    }) {
      const data = _.get(getPlannerElementNodeSetsFromIds, "multiNode", []);

      return {
        plannerElementNodeSets: data,
        isData: !_.isEmpty(data) && isData,
        isLoading:
          getPlannerElementNodeSetsFromIds["networkStatus"] == 1 ||
          getPlannerElementNodeSetsFromIds["networkStatus"] == 2 ||
          isLoading,
      };
    },
  }
);
